import axios from 'axios';
import type { SchoolFocusGroupReport } from '../../services/focusGroupService';

const BASE_URL = process.env.REACT_APP_BASE_URL;

export const FOCUS_GROUP_REPORT_POC_PATH = '/focus-group-report-poc';

export type GenerationSummary = {
  id: string;
  status: string;
  model_id: string;
  prompt_hash: string;
  created_at: string;
  created_by: string;
  error: string | null;
};

export type QuoteVerification = {
  verdict: string;
  detail?: string | null;
};

export type GeneratedQuote = {
  text: string;
  speaker_id: string;
  data_file_id: string;
  focus_group_name: string;
  observation?: string;
  verification?: QuoteVerification | null;
};

export type GeneratedTakeaway = {
  content: string;
  quotes: GeneratedQuote[];
};

export type GeneratedTheme = {
  name: string;
  takeaways: GeneratedTakeaway[];
};

export type GeneratedReport = {
  summary: string;
  themes: GeneratedTheme[];
};

export type GenerationDetail = GenerationSummary & {
  result: GeneratedReport | null;
  agent_messages: unknown;
};

export type VerificationQuote = {
  text: string;
  data_file_id: string;
  speaker_id: string;
  verdict: string | null;
  detail: string | null;
};

export type GenerateStreamHandlers = {
  onThinking?: (text: string) => void;
  onToolCall?: (toolName: string) => void;
  onVerification?: (round: number, quotes: VerificationQuote[]) => void;
  signal?: AbortSignal;
};

export type GenerateDone = {
  generation_id: string;
  status: string;
  result: GeneratedReport | null;
  error: string | null;
};

const generationsUrl = (eoaId: string, schoolId: string) =>
  `${BASE_URL}/eoa/${eoaId}/schools/${schoolId}/focus_group_report_generations`;

const authHeaders = (accessToken: string) => ({ Authorization: `Bearer ${accessToken}` });

export const listGenerations = async (
  eoaId: string,
  schoolId: string,
  accessToken: string
): Promise<GenerationSummary[]> => {
  const response = await axios.get<{ generations: GenerationSummary[] }>(generationsUrl(eoaId, schoolId), {
    headers: authHeaders(accessToken),
  });
  return response.data.generations;
};

export const getGeneration = async (
  eoaId: string,
  schoolId: string,
  generationId: string,
  accessToken: string
): Promise<GenerationDetail> => {
  const response = await axios.get<GenerationDetail>(`${generationsUrl(eoaId, schoolId)}/${generationId}`, {
    headers: authHeaders(accessToken),
  });
  return response.data;
};

export const getProductionFocusGroupReport = async (
  eoaId: string,
  schoolId: string,
  accessToken: string
): Promise<SchoolFocusGroupReport | null> => {
  try {
    const response = await axios.get<SchoolFocusGroupReport>(
      `${BASE_URL}/eoa/${eoaId}/schools/${schoolId}/focus_group_report`,
      { headers: authHeaders(accessToken) }
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) return null;
    throw error;
  }
};

const readSseStream = async (response: Response, onEvent: (event: Record<string, unknown>) => void): Promise<void> => {
  if (!response.body) throw new Error('Response has no body to stream');

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  const drainBuffer = () => {
    let sepIndex = buffer.indexOf('\n\n');
    while (sepIndex !== -1) {
      const frame = buffer.slice(0, sepIndex);
      buffer = buffer.slice(sepIndex + 2);
      const dataLine = frame.split('\n').find((line) => line.startsWith('data:'));
      if (dataLine) onEvent(JSON.parse(dataLine.slice('data:'.length).trim()));
      sepIndex = buffer.indexOf('\n\n');
    }
  };

  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    drainBuffer();
  }
};

const errorDetail = async (response: Response): Promise<string> => {
  try {
    const body = await response.json();
    if (typeof body?.detail === 'string') return body.detail;
  } catch {
    // ignore
  }
  return `Request failed with status ${response.status}`;
};

export const postGenerateStream = async (
  eoaId: string,
  schoolId: string,
  accessToken: string,
  handlers: GenerateStreamHandlers = {}
): Promise<GenerateDone> => {
  const response = await fetch(generationsUrl(eoaId, schoolId), {
    method: 'POST',
    headers: authHeaders(accessToken),
    signal: handlers.signal,
  });

  if (!response.ok) {
    throw new Error(await errorDetail(response));
  }

  let done: GenerateDone | null = null;

  await readSseStream(response, (event) => {
    switch (event.type) {
      case 'thinking':
        if (typeof event.text === 'string') handlers.onThinking?.(event.text);
        break;
      case 'tool_call':
        if (typeof event.tool_name === 'string') handlers.onToolCall?.(event.tool_name);
        break;
      case 'verification':
        handlers.onVerification?.(event.round as number, (event.quotes as VerificationQuote[]) ?? []);
        break;
      case 'done':
        done = {
          generation_id: String(event.generation_id),
          status: String(event.status),
          result: (event.result as GeneratedReport | null) ?? null,
          error: (event.error as string | null) ?? null,
        };
        break;
      case 'error':
        throw new Error(typeof event.detail === 'string' ? event.detail : 'Generation stream error');
    }
  });

  if (done === null) throw new Error('Generation stream ended without a completion event');
  return done;
};
